export function formatNum(x: number, digits: number): string {
  return x.toString().padStart(digits, "0");
}

/**
 * Formats a byte number into a human-readable string with units (B, KB, MB, GB, TB).
 *
 * @param {number | null | undefined} bytes - Number of bytes to format
 * @param {number} [decimals=1] - Number of decimal places to preserve (defaults to 1)
 * @returns {string} Human-readable byte size string (e.g. "1.5 MB", "0 B")
 */
export function formatBytes(bytes?: number | null, decimals: number = 1): string {
  if (bytes === null || bytes === undefined || isNaN(bytes) || bytes <= 0) {
    return "0 B";
  }
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  if (i === 0) {
    return `${bytes} B`;
  }
  return `${(bytes / Math.pow(k, i)).toFixed(decimals)} ${sizes[i]}`;
}

/**
 * Computes exact UTF-8 byte length for a string across Node and browser/RN runtimes.
 *
 * @param {string} str - Input string to measure.
 * @returns {number} UTF-8 byte length.
 */
export function getUtf8ByteLength(str: string): number {
  if (!str) return 0;
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(str).length;
  }
  let s = str.length;
  for (let i = str.length - 1; i >= 0; i--) {
    const code = str.charCodeAt(i);
    if (code > 0x7f && code <= 0x7ff) s++;
    else if (code > 0x7ff && code <= 0xffff) s += 2;
    if (code >= 0xdc00 && code <= 0xdfff) i--;
  }
  return s;
}

/**
 * Escapes HTML special characters to prevent XSS injection when building HTML strings.
 *
 * @param {string | undefined | null} text - Raw text to escape.
 * @returns {string} HTML-safe escaped string.
 */
export function escapeHtml(text?: string | null): string {
  if (!text) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Formats media playback duration in milliseconds into mm:ss format.
 *
 * @param {number} [durationMs] - Duration in milliseconds.
 * @returns {string} Formatted duration string (e.g. "2:05") or empty string.
 */
export function formatMediaPlaybackDuration(durationMs?: number): string {
  if (!durationMs || isNaN(durationMs) || durationMs <= 0) return "";
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
}



